import type {
  GateClassificationInput,
  GateClassifiedProtocol,
  GateTrigger,
  BiomarkerDeviation,
  HighRiskMedicationMatch,
  RxCUI,
  Gate,
  ApprovalStatus,
} from '../../types/gateClassification';
import { NON_NORMAL_BIOMARKERS } from '../../types/gateClassification';
import type { BiomarkerResult } from '../../types/index';
import { HIGH_RISK_MEDICATION_CLASSES } from './highRiskMedicationClasses';

// ── Name-based fallback for high-risk medication matching ──

export const HIGH_RISK_MEDICATION_NAME_FALLBACK: Record<string, Set<string>> = {
  anticoagulants: new Set([
    'warfarin', 'coumadin', 'heparin', 'enoxaparin', 'lovenox',
    'rivaroxaban', 'xarelto', 'apixaban', 'eliquis', 'dabigatran',
    'pradaxa', 'edoxaban', 'savaysa',
  ]),
  immunosuppressants: new Set([
    'cyclosporine', 'neoral', 'sandimmune', 'tacrolimus', 'prograf',
    'mycophenolate', 'cellcept', 'azathioprine', 'imuran', 'sirolimus',
    'rapamune', 'everolimus', 'zortress',
  ]),
  cardiac_drugs: new Set([
    'digoxin', 'lanoxin', 'amiodarone', 'cordarone', 'pacerone',
    'flecainide', 'tambocor', 'sotalol', 'betapace', 'dofetilide', 'tikosyn',
  ]),
  insulin_and_analogs: new Set([
    'insulin', 'insulin glargine', 'lantus', 'basaglar', 'insulin lispro',
    'humalog', 'insulin aspart', 'novolog', 'insulin detemir', 'levemir',
  ]),
  chemotherapy: new Set([
    'methotrexate', 'trexall', 'cyclophosphamide', 'cytoxan', 'doxorubicin',
    'adriamycin', 'cisplatin', 'platinol', 'fluorouracil', '5-fu',
  ]),
  maois: new Set([
    'phenelzine', 'nardil', 'tranylcypromine', 'parnate', 'isocarboxazid',
    'marplan', 'selegiline', 'emsam',
  ]),
  lithium: new Set(['lithium', 'lithobid', 'eskalith']),
  antiepileptics: new Set([
    'phenytoin', 'dilantin', 'carbamazepine', 'tegretol', 'valproic acid',
    'valproate', 'depakote', 'depakene', 'phenobarbital', 'luminal',
    'lamotrigine', 'lamictal',
  ]),
  GLP1_AGONIST: new Set([
    'semaglutide', 'ozempic', 'wegovy', 'rybelsus', 'tirzepatide',
    'mounjaro', 'zepbound', 'liraglutide', 'victoza', 'saxenda',
    'dulaglutide', 'trulicity',
  ]),
};

// ── Classifier Options ──

export interface ClassifierOptions {
  onClassificationError?: (error: Error) => void;
}

// ── Criterion 1: Medication Count ──

export function evaluateMedicationCount(medications: string[]): GateTrigger | null {
  if (medications.length < 2) return null;
  return {
    type: 'MEDICATION_COUNT',
    detail: `${medications.length} medications (threshold: 2)`,
    sourceField: 'patientProfile.medications',
    auditPriority: 'MEDIUM',
  };
}

// ── Criterion 2: High-Risk Medication ──

export function checkHighRiskMedication(
  medication: string,
  medicationRxCui?: RxCUI,
): HighRiskMedicationMatch {
  if (medication === '') return { isHighRisk: false, medication: '' };

  // Primary: RxCUI-based lookup
  if (medicationRxCui) {
    for (const [drugClass, rxCuis] of Object.entries(HIGH_RISK_MEDICATION_CLASSES)) {
      if (rxCuis.has(medicationRxCui)) {
        return { isHighRisk: true, medication, drugClass, rxCui: medicationRxCui };
      }
    }
  }

  // Fallback: normalized name matching
  const normalized = medication.toLowerCase().trim();
  for (const [drugClass, names] of Object.entries(HIGH_RISK_MEDICATION_NAME_FALLBACK)) {
    if (names.has(normalized)) {
      return { isHighRisk: true, medication, drugClass };
    }
  }

  return { isHighRisk: false, medication };
}

export function evaluateHighRiskMedications(
  medications: string[],
  medicationRxCuis?: Map<string, RxCUI>,
): GateTrigger[] {
  const triggers: GateTrigger[] = [];
  for (const med of medications) {
    const rxCui = medicationRxCuis?.get(med.toLowerCase());
    const match = checkHighRiskMedication(med, rxCui);
    if (match.isHighRisk) {
      triggers.push({
        type: 'HIGH_RISK_MEDICATION',
        detail: `${match.medication} (${match.drugClass})`,
        sourceField: 'patientProfile.medications',
        auditPriority: 'HIGH',
      });
    }
  }
  return triggers;
}

// ── Criterion 3: Pregnancy / Breastfeeding ──

export function evaluatePregnancy(
  isPregnant?: boolean,
  isBreastfeeding?: boolean,
): GateTrigger[] {
  const triggers: GateTrigger[] = [];
  if (isPregnant === true) {
    triggers.push({
      type: 'PREGNANCY',
      detail: 'Patient is pregnant',
      sourceField: 'patientProfile.isPregnant',
      auditPriority: 'HIGH',
    });
  }
  if (isBreastfeeding === true) {
    triggers.push({
      type: 'BREASTFEEDING',
      detail: 'Patient is breastfeeding',
      sourceField: 'patientProfile.isBreastfeeding',
      auditPriority: 'HIGH',
    });
  }
  return triggers;
}

// ── Criterion 4: Biomarker Deviations ──

export function calculateBiomarkerDeviation(
  biomarkerName: string,
  value: number,
  optimalLow: number,
  optimalHigh: number,
  unit: string,
): BiomarkerDeviation | null {
  // Invalid range checks
  if (optimalLow >= optimalHigh) return null;
  if (optimalHigh <= 0) return null;

  const midpoint = (optimalLow + optimalHigh) / 2;
  const halfRange = (optimalHigh - optimalLow) / 2;
  const sd = (value - midpoint) / halfRange;

  const isNonNormal = NON_NORMAL_BIOMARKERS.has(biomarkerName.toLowerCase().trim());

  if (isNonNormal) {
    // Range-based fallback for non-normal biomarkers
    const triggered = value < optimalLow * 0.5 || value > optimalHigh * 2.0;
    if (!triggered) return null;
    // Still calculate approximate SD for reporting
    return {
      biomarkerName,
      patientValue: value,
      unit,
      optimalLow,
      optimalHigh,
      sdFromOptimal: Math.round(Math.abs(sd) * 100) / 100,
      direction: value > midpoint ? 'above' : 'below',
    };
  }

  // Normal biomarkers: SD-based
  if (Math.abs(sd) <= 2.0) return null;

  return {
    biomarkerName,
    patientValue: value,
    unit,
    optimalLow,
    optimalHigh,
    sdFromOptimal: Math.round(Math.abs(sd) * 100) / 100,
    direction: value > midpoint ? 'above' : 'below',
  };
}

export function evaluateBiomarkerDeviations(
  biomarkerResults: BiomarkerResult[],
): { triggers: GateTrigger[]; deviations: BiomarkerDeviation[] } {
  const triggers: GateTrigger[] = [];
  const deviations: BiomarkerDeviation[] = [];

  for (const br of biomarkerResults) {
    const range = br.functionalRange ?? br.standardRange;
    if (!range) continue;

    const deviation = calculateBiomarkerDeviation(
      br.biomarker,
      br.value,
      range.low,
      range.high,
      br.unit,
    );

    if (deviation) {
      deviations.push(deviation);
      triggers.push({
        type: 'BIOMARKER_DEVIATION',
        detail: `${deviation.biomarkerName}: ${deviation.patientValue} ${deviation.unit} (${deviation.sdFromOptimal} SD ${deviation.direction})`,
        sourceField: `biomarkerResults.${deviation.biomarkerName}`,
        auditPriority: 'MEDIUM',
      });
    }
  }

  return { triggers, deviations };
}

// ── Criterion 5a: Pre-Filter Exclusions ──

export function evaluatePreFilterExclusions(
  exclusions: GateClassificationInput['preFilterExclusions'],
): GateTrigger[] {
  const triggers: GateTrigger[] = [];
  for (const ex of exclusions) {
    if (ex.severity === 'block') {
      triggers.push({
        type: 'CONTRAINDICATION_RULE',
        detail: `${ex.productName}: ${ex.reason}`,
        sourceField: `preFilterExclusions.${ex.ruleId}`,
        auditPriority: 'HIGH',
      });
    }
  }
  return triggers;
}

// ── Criterion 5b: Contraindication Rules ──

export function evaluateContraindicationRules(
  violations: GateClassificationInput['safetyViolations'],
): GateTrigger[] {
  const triggers: GateTrigger[] = [];
  for (const v of violations) {
    if (v.severity === 'block') {
      triggers.push({
        type: 'CONTRAINDICATION_RULE',
        detail: `${v.ruleName}: ${v.message}`,
        sourceField: `safetyViolations.${v.ruleId}`,
        auditPriority: 'HIGH',
      });
    }
  }
  return triggers;
}

// ── Criterion 6: Product Tiers ──

export function evaluateProductTiers(
  products: GateClassificationInput['productDetails'],
): GateTrigger[] {
  const highTierProducts = products.filter(p => p.tier >= 2);
  if (highTierProducts.length === 0) return [];

  const detail = highTierProducts.map(p => `${p.name} (Tier ${p.tier})`).join(', ');
  return [{
    type: 'PRODUCT_TIER',
    detail,
    sourceField: 'productDetails',
    auditPriority: 'HIGH',
  }];
}

// ── Criterion 7: Conservative Defaults ──

export function evaluateConservativeDefaults(
  conservativeDefaultsApplied: boolean,
  defaultFields: string[],
): GateTrigger | null {
  if (conservativeDefaultsApplied !== true) return null;
  if (defaultFields.length === 0) return null;

  return {
    type: 'CONSERVATIVE_DEFAULT',
    detail: `Conservative defaults applied to: ${defaultFields.join(', ')}`,
    sourceField: 'conservativeDefaultFields',
    auditPriority: 'MEDIUM',
  };
}

// ── Classification ID ──

/**
 * Generate a unique gate classification ID.
 */
export function generateClassificationId(): string {
  return `gc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

// ── Gate Determination ──

export function determineGate(triggers: GateTrigger[]): Gate {
  return triggers.length === 0 ? 'GATE_1' : 'GATE_2';
}

// ── Reasoning Builder ──

export function buildReasoning(gate: Gate, triggers: GateTrigger[]): string {
  if (gate === 'GATE_1') {
    return 'Gate 1: No triggers fired. All criteria passed.';
  }
  const triggerTypes = triggers.map(t => t.type).join(', ');
  return `Gate 2: ${triggers.length} trigger(s) fired: ${triggerTypes}`;
}

// ── Approval Status ──

export function determineApprovalStatus(gate: Gate): ApprovalStatus {
  return gate === 'GATE_1' ? 'GATE_1_APPROVED' : 'PENDING_PROVIDER_REVIEW';
}

// ── Main Classifier ──

export function classifyGate(
  input: GateClassificationInput,
  options?: ClassifierOptions,
): GateClassifiedProtocol {
  try {
    const triggers: GateTrigger[] = [];

    // Criterion 1: Medication count
    const medCountTrigger = evaluateMedicationCount(input.patientProfile.medications);
    if (medCountTrigger) triggers.push(medCountTrigger);

    // Criterion 2: High-risk medications (R2 — PRO.02 RxCUI lookup)
    const highRiskTriggers = evaluateHighRiskMedications(
      input.patientProfile.medications,
      input.medicationRxCuis,
    );
    triggers.push(...highRiskTriggers);

    // Criterion 3: Pregnancy / Breastfeeding
    const pregnancyTriggers = evaluatePregnancy(
      input.patientProfile.isPregnant,
      input.patientProfile.isBreastfeeding,
    );
    triggers.push(...pregnancyTriggers);

    // Criterion 4: Biomarker deviations
    const { triggers: bioTriggers } = evaluateBiomarkerDeviations(input.biomarkerResults);
    triggers.push(...bioTriggers);

    // Criterion 5a: Pre-filter exclusions
    const exclusionTriggers = evaluatePreFilterExclusions(input.preFilterExclusions);
    triggers.push(...exclusionTriggers);

    // Criterion 5b: Contraindication rules
    const contraindicationTriggers = evaluateContraindicationRules(input.safetyViolations);
    triggers.push(...contraindicationTriggers);

    // Criterion 6: Product tiers
    const tierTriggers = evaluateProductTiers(input.productDetails);
    triggers.push(...tierTriggers);

    // Criterion 7: Conservative defaults
    const defaultTrigger = evaluateConservativeDefaults(
      input.conservativeDefaultsApplied,
      input.conservativeDefaultFields,
    );
    if (defaultTrigger) triggers.push(defaultTrigger);

    const gate = determineGate(triggers);
    const reasoning = buildReasoning(gate, triggers);
    const approvalStatus = determineApprovalStatus(gate);

    return {
      protocol: input.protocol,
      gate,
      triggers,
      reasoning,
      providerSummary: null,
      classifiedAt: new Date().toISOString(),
      classificationId: generateClassificationId(),
      approvalStatus,
    };
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    options?.onClassificationError?.(error);

    const fallbackProtocol = input?.protocol ?? {
      id: 'unknown',
      createdAt: new Date().toISOString(),
      supplements: [],
      summary: '',
      tierRecommendation: 1 as any,
      warnings: [],
    };

    const errorTrigger: GateTrigger = {
      type: 'CLASSIFICATION_ERROR',
      detail: `Classification error: ${error.message}`,
      sourceField: 'classifyGate',
      auditPriority: 'HIGH',
    };

    return {
      protocol: fallbackProtocol,
      gate: 'GATE_2',
      triggers: [errorTrigger],
      reasoning: 'Gate 2: 1 trigger(s) fired: CLASSIFICATION_ERROR',
      providerSummary: null,
      classifiedAt: new Date().toISOString(),
      classificationId: generateClassificationId(),
      approvalStatus: 'PENDING_PROVIDER_REVIEW',
    };
  }
}

// ── Gate Storage Backend (PRO.08 D-4) ──

/**
 * Storage backend for persisting gate classifications.
 */
export interface GateStorageBackend {
  /** Write a gate classification result to storage */
  writeClassification(classificationId: string, data: GateClassifiedProtocol): Promise<void>;
  /** Query classifications by protocol ID */
  queryByProtocolId(protocolId: string): Promise<GateClassifiedProtocol[]>;
}

/**
 * Persist a gate classification result to storage (PRO.08 D-4).
 * Called after classifyGate to ensure classification is durable.
 */
export async function persistClassification(
  classification: GateClassifiedProtocol,
  storage: GateStorageBackend,
): Promise<{ success: boolean; errorMessage?: string }> {
  try {
    await storage.writeClassification(classification.classificationId, classification);
    return { success: true };
  } catch (err) {
    return {
      success: false,
      errorMessage: `Gate classification persistence failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}

// ── Provider Clinical Summary (PRO.08 D-5) ──

/**
 * Build a clinical summary for provider review.
 * Assembles trigger details, biomarker deviations, and patient profile
 * into a structured document the provider can review.
 */
export function buildProviderClinicalSummary(params: {
  triggers: GateTrigger[];
  deviations: BiomarkerDeviation[];
  patientProfile: {
    age?: number;
    sex?: string;
    medications: string[];
    conditions: string[];
    isPregnant?: boolean;
    isBreastfeeding?: boolean;
  };
  classificationId: string;
}): {
  classificationId: string;
  triggerSummary: string;
  deviationSummary: string;
  patientContext: string;
  reviewGuidance: string;
} {
  const triggerLines = params.triggers.map(
    (t) => `- [${t.type}] ${t.detail} (priority: ${t.auditPriority})`,
  );

  const deviationLines = params.deviations.map(
    (d) => `- ${d.biomarkerName}: ${d.patientValue} ${d.unit} (${d.sdFromOptimal} SD ${d.direction})`,
  );

  const profileParts: string[] = [];
  if (params.patientProfile.age !== undefined) profileParts.push(`Age: ${params.patientProfile.age}`);
  if (params.patientProfile.sex) profileParts.push(`Sex: ${params.patientProfile.sex}`);
  if (params.patientProfile.isPregnant) profileParts.push('Pregnant: Yes');
  if (params.patientProfile.isBreastfeeding) profileParts.push('Breastfeeding: Yes');
  if (params.patientProfile.medications.length > 0) {
    profileParts.push(`Medications: ${params.patientProfile.medications.join(', ')}`);
  }
  if (params.patientProfile.conditions.length > 0) {
    profileParts.push(`Conditions: ${params.patientProfile.conditions.join(', ')}`);
  }

  const highPriorityCount = params.triggers.filter((t) => t.auditPriority === 'HIGH').length;

  return {
    classificationId: params.classificationId,
    triggerSummary: triggerLines.length > 0 ? triggerLines.join('\n') : 'No triggers',
    deviationSummary: deviationLines.length > 0 ? deviationLines.join('\n') : 'No significant deviations',
    patientContext: profileParts.join(' | '),
    reviewGuidance: highPriorityCount > 0
      ? `${highPriorityCount} HIGH priority trigger(s) require immediate review.`
      : 'Standard review. No high-priority items.',
  };
}

// ── Approval State Machine (PRO.08 D-6) ──

/** Valid approval state transitions */
const APPROVAL_TRANSITIONS: Record<ApprovalStatus, ApprovalStatus[]> = {
  GATE_1_APPROVED: ['GATE_1_APPROVED'], // Terminal — no transitions
  PENDING_PROVIDER_REVIEW: ['PROVIDER_APPROVED', 'PROVIDER_REJECTED', 'REVALIDATION_FAILED'],
  PROVIDER_APPROVED: ['PROVIDER_APPROVED'], // Terminal
  PROVIDER_REJECTED: ['PENDING_PROVIDER_REVIEW'], // Can be re-submitted
  PROVIDER_MODIFIED: ['PENDING_PROVIDER_REVIEW', 'PROVIDER_APPROVED', 'PROVIDER_REJECTED'], // Pending re-validation
  REVALIDATION_FAILED: ['PENDING_PROVIDER_REVIEW', 'PROVIDER_REJECTED'], // Can retry or reject
};

/**
 * Check if an approval status transition is valid.
 */
export function isValidApprovalTransition(
  from: ApprovalStatus,
  to: ApprovalStatus,
): boolean {
  return APPROVAL_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * Formal approval state machine with transition validation.
 */
export class ApprovalStateMachine {
  private _status: ApprovalStatus;
  private _history: Array<{ from: ApprovalStatus; to: ApprovalStatus; timestamp: string; actor?: string }> = [];

  constructor(initialStatus: ApprovalStatus) {
    this._status = initialStatus;
  }

  get status(): ApprovalStatus {
    return this._status;
  }

  get history(): ReadonlyArray<{ from: ApprovalStatus; to: ApprovalStatus; timestamp: string; actor?: string }> {
    return this._history;
  }

  /**
   * Attempt a status transition. Throws if transition is invalid.
   */
  transition(to: ApprovalStatus, actor?: string): void {
    if (!isValidApprovalTransition(this._status, to)) {
      throw new Error(
        `Invalid approval transition: ${this._status} → ${to}. ` +
        `Valid transitions: ${APPROVAL_TRANSITIONS[this._status]?.join(', ') || 'none'}`,
      );
    }

    this._history.push({
      from: this._status,
      to,
      timestamp: new Date().toISOString(),
      actor,
    });
    this._status = to;
  }
}
